// Text primitives shared by every block layout. Everything here works in
// canonical preview pixels (800px wide layout) and never touches the DOM;
// width and font metrics come from an injected measurer so layouts can be
// unit tested without a canvas.

// Tailwind preflight font stack and line-height inherited from <html>.
export const DEFAULT_FONT_FAMILY =
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';
export const INHERITED_LINE_HEIGHT = 1.5;
export const DEFAULT_TEXT_COLOR = "#ffffff";
export const BULLET_FONT_FAMILY = "Arial, sans-serif";

const GENERIC_FAMILIES = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-sans-serif",
  "ui-serif",
  "ui-monospace",
  "ui-rounded",
]);

const LAYOUT_EPSILON = 0.01;

export const formatFontFamily = (family) => {
  if (!family || typeof family !== "string") return DEFAULT_FONT_FAMILY;
  const trimmed = family.trim();
  if (!trimmed) return DEFAULT_FONT_FAMILY;
  if (trimmed.includes(",")) return trimmed;
  const bare = trimmed.replace(/^["']|["']$/g, "");
  if (GENERIC_FAMILIES.has(bare.toLowerCase())) return bare;
  return `"${bare}"`;
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const toPx = (value, fallback = 0) => {
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  const n = toFiniteNumber(value);
  return n === null ? fallback : n;
};

/**
 * Resolve a Redux fontSchema entry into the canvas font descriptor used by
 * the measurer and the painter.
 */
export function resolveFont(style, { size = 18, kerning = false, ...overrides } = {}) {
  const src = style || {};
  const fontSize = toPx(src.fontSize, size) || size;
  return {
    family: src.fontFamily || null,
    size: fontSize,
    weight: src.fontWeight || 400,
    italic: src.fontStyle === "italic",
    letterSpacing: toPx(src.letterSpacing, 0),
    transform: src.textTransform || "none",
    kerning,
    ...overrides,
  };
}

export const fontKey = (font) =>
  [
    font.italic ? "i" : "n",
    font.weight,
    font.size,
    font.family || "",
    font.letterSpacing,
    font.kerning ? "k" : "-",
  ].join("|");

export const toCanvasFont = (font) =>
  `${font.italic ? "italic " : ""}${font.weight} ${font.size}px ${formatFontFamily(
    font.family,
  )}`;

/** CSS line-height resolution: unitless multiplier, otherwise inherited. */
export const resolveLineHeightPx = (font, lineHeight) => {
  const n = toFiniteNumber(lineHeight);
  return font.size * (n === null ? INHERITED_LINE_HEIGHT : n);
};

export const applyTextTransform = (text, transform) => {
  const source = text == null ? "" : String(text);
  switch (transform) {
    case "uppercase":
      return source.toUpperCase();
    case "lowercase":
      return source.toLowerCase();
    case "capitalize":
      return source.replace(
        /(^|[\s\-‐(["'])(\p{L})/gu,
        (match, lead, letter) => lead + letter.toUpperCase(),
      );
    default:
      return source;
  }
};

/** Collapse whitespace the way `white-space: normal` does. */
export const collapseWhitespace = (text) =>
  String(text == null ? "" : text)
    .replace(/[\t\n\r\f ]+/g, " ")
    .trim();

// Break opportunities: spaces, and after a hyphen that is followed by more
// text (CSS line breaking allows "Jean-|Luc").
const toBreakUnits = (text) => {
  const units = [];
  text.split(" ").forEach((word, wordIndex) => {
    if (!word) return;
    word.split(/(?<=-)(?=[^-])/).forEach((part, partIndex) => {
      units.push({ text: part, spaceBefore: wordIndex > 0 && partIndex === 0 });
    });
  });
  if (units.length) units[0].spaceBefore = false;
  return units;
};

const breakLongWord = (measurer, font, word, maxWidth) => {
  const chars = Array.from(word);
  const pieces = [];
  let current = "";
  chars.forEach((ch) => {
    const candidate = current + ch;
    if (current && measurer.width(font, candidate) > maxWidth + LAYOUT_EPSILON) {
      pieces.push(current);
      current = ch;
    } else {
      current = candidate;
    }
  });
  if (current) pieces.push(current);
  return pieces;
};

/**
 * Greedy line breaking matching the browser for Latin text.
 * - text should already be transformed and whitespace-collapsed.
 * - breakWords mirrors `word-break: break-word` (split words that cannot
 *   fit on their own line); otherwise long words overflow.
 */
export function wrapText(measurer, font, text, maxWidth, { breakWords = false } = {}) {
  if (!text) return [""];
  if (!(maxWidth > 0)) return [text];
  if (measurer.width(font, text) <= maxWidth + LAYOUT_EPSILON) return [text];

  const units = toBreakUnits(text);
  const lines = [];
  let current = "";

  const pushUnitAlone = (unitText) => {
    if (breakWords && measurer.width(font, unitText) > maxWidth + LAYOUT_EPSILON) {
      const pieces = breakLongWord(measurer, font, unitText, maxWidth);
      lines.push(...pieces.slice(0, -1));
      return pieces[pieces.length - 1] || "";
    }
    return unitText;
  };

  units.forEach((unit) => {
    if (!current) {
      current = pushUnitAlone(unit.text);
      return;
    }
    const candidate = current + (unit.spaceBefore ? " " : "") + unit.text;
    if (measurer.width(font, candidate) <= maxWidth + LAYOUT_EPSILON) {
      current = candidate;
    } else {
      lines.push(current);
      current = pushUnitAlone(unit.text);
    }
  });
  if (current || !lines.length) lines.push(current);
  return lines;
}

/** Single-line max-content width after transform and collapse. */
export const measureMaxContent = (measurer, font, text) =>
  measurer.width(font, collapseWhitespace(applyTextTransform(text, font.transform)));

export const prepareText = (text, font) =>
  collapseWhitespace(applyTextTransform(text, font.transform));

/** Baseline offset of text inside a CSS line box of height `lineHeightPx`. */
export const baselineInLineBox = (metrics, lineHeightPx) =>
  (lineHeightPx - (metrics.ascent + metrics.descent)) / 2 + metrics.ascent;

const alignLineX = (align, left, width, lineWidth) => {
  if (lineWidth > width + LAYOUT_EPSILON) return left;
  switch (align) {
    case "center":
      return left + (width - lineWidth) / 2;
    case "right":
    case "end":
      return left + width - lineWidth;
    default:
      return left;
  }
};

export const createTextOp = ({
  text,
  x,
  baseline,
  font,
  color,
  decoration,
  width,
  lineTop,
  lineBottom,
  meta,
}) => ({
  kind: "text",
  text,
  x,
  y: baseline,
  font,
  color: color || DEFAULT_TEXT_COLOR,
  decoration: decoration && decoration !== "none" ? decoration : null,
  width,
  top: lineTop,
  bottom: lineBottom,
  ...(meta ? { meta } : null),
});

/**
 * Emit one line of text at a known baseline. Handles alignment and
 * justification (justify spreads inter-word space unless `isLastLine`).
 */
export function emitLine(ops, {
  measurer,
  font,
  text,
  left,
  width,
  baseline,
  align = "left",
  color,
  decoration,
  lineTop,
  lineBottom,
  isLastLine = true,
  meta,
}) {
  if (!text) return;
  const lineWidth = measurer.width(font, text);
  if (align === "justify" && !isLastLine && text.includes(" ") && lineWidth < width) {
    const words = text.split(" ");
    const spaceWidth = measurer.width(font, " ");
    const extra = (width - lineWidth) / (words.length - 1);
    let x = left;
    words.forEach((word) => {
      const wordWidth = word ? measurer.width(font, word) : 0;
      const wordX = x;
      x += wordWidth + spaceWidth + extra;
      if (!word) return;
      ops.push(
        createTextOp({
          text: word,
          x: wordX,
          baseline,
          font,
          color,
          decoration,
          width: wordWidth,
          lineTop,
          lineBottom,
          meta,
        }),
      );
    });
    return;
  }
  ops.push(
    createTextOp({
      text,
      x: alignLineX(align === "justify" ? "left" : align, left, width, lineWidth),
      baseline,
      font,
      color,
      decoration,
      width: lineWidth,
      lineTop,
      lineBottom,
      meta,
    }),
  );
}

/**
 * Lay out a paragraph: wrap, then stack lines of `lineHeightPx`.
 * Returns the paragraph height (lines × line height).
 */
export function layoutParagraph(ops, {
  measurer,
  font,
  text,
  left,
  width,
  top,
  align = "left",
  lineHeightPx,
  color,
  decoration,
  breakWords = false,
  preformatted = false,
  meta,
}) {
  const prepared = preformatted ? text : prepareText(text, font);
  if (!prepared) return 0;
  const lines = wrapText(measurer, font, prepared, width, { breakWords });
  const metrics = measurer.metrics(font);
  const baseOffset = baselineInLineBox(metrics, lineHeightPx);
  lines.forEach((line, i) => {
    const lineTop = top + i * lineHeightPx;
    emitLine(ops, {
      measurer,
      font,
      text: line,
      left,
      width,
      baseline: lineTop + baseOffset,
      align,
      color,
      decoration,
      lineTop,
      lineBottom: lineTop + lineHeightPx,
      isLastLine: i === lines.length - 1,
      meta,
    });
  });
  return lines.length * lineHeightPx;
}

/**
 * Compose one table/grid row whose cells are baseline aligned
 * (`vertical-align: baseline`). Each cell: { lineHeightPx, font }.
 * Returns { height, baseline } relative to the row top.
 */
export function composeBaselineRow(measurer, cells) {
  let above = 0;
  let below = 0;
  cells.forEach((cell) => {
    const metrics = measurer.metrics(cell.font);
    const offset = baselineInLineBox(metrics, cell.lineHeightPx);
    above = Math.max(above, offset);
    below = Math.max(below, cell.lineHeightPx - offset);
  });
  return { height: above + below, baseline: above };
}
